import dayjs from 'dayjs'
import {
  CATCH_BOARD_STAGES,
  getCatchBoardStageMeta,
  isCatchBoardOpenStage,
  isCatchBoardReadiness,
} from 'src/screens/catch-board/catchBoardConstants'

function mapContactSnapshot(contact = {}) {
  if (!contact || typeof contact !== 'object') {
    return {
      accountId: null,
      name: '',
      email: '',
      role: '',
      phone: '',
    }
  }

  return {
    accountId: contact.accountId || null,
    name: contact.name || '',
    email: contact.email || '',
    role: contact.role || '',
    phone: contact.phone || '',
  }
}

function mapActivityLogEntry(entry = {}) {
  return {
    activityId: entry.activityId || null,
    at: entry.at || null,
    accountId: entry.accountId || null,
    name: entry.name || '',
    email: entry.email || '',
    type: entry.type || '',
    summary: entry.summary || '',
    metadata: entry.metadata || {},
  }
}

/**
 * Format integer pence as GBP for cards and column totals.
 * @param {number|null|undefined} pence
 * @param {{ empty?: string, compact?: boolean }} [options]
 */
export function formatCatchBoardPence(pence, { empty = '—', compact = false } = {}) {
  if (pence == null || pence === '') {
    return empty
  }

  const value = Number(pence)
  if (!Number.isFinite(value)) {
    return empty
  }

  const pounds = value / 100

  if (compact) {
    const abs = Math.abs(pounds)
    if (abs >= 1_000_000) {
      const formatted = `${(pounds / 1_000_000).toFixed(1)}m`
      return pounds < 0 ? `-£${formatted.slice(1)}` : `£${formatted}`
    }
    if (abs >= 10_000) {
      const formatted = `${Math.round(pounds / 1000)}k`
      return pounds < 0 ? `-£${formatted.slice(1)}` : `£${formatted}`
    }
  }

  const formatted = pounds.toLocaleString('en-GB', {
    minimumFractionDigits: pounds % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })

  return pounds < 0 ? `-£${formatted.slice(1)}` : `£${formatted}`
}

export function formatCatchBoardDate(value, { empty = '—' } = {}) {
  if (!value) {
    return empty
  }

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('D MMM YYYY') : empty
}

/**
 * Compact Landed-card summary: plan, won MRR, optional go-live.
 * Quiet when stage is not Landed or no won fields are set.
 *
 * @param {{
 *   stage?: string|null,
 *   wonPlan?: string|null,
 *   wonMrrPence?: number|null,
 *   wonMrrLabel?: string|null,
 *   wonGoLiveDate?: string|null,
 *   wonGoLiveLabel?: string|null,
 * }} [deal]
 * @returns {{
 *   primary: string|null,
 *   goLiveLabel: string|null,
 *   title: string,
 * }|null}
 */
export function getCatchBoardLandedCardDisplay(deal = {}) {
  if (deal?.stage !== 'landed') {
    return null
  }

  const plan = String(deal.wonPlan || '').trim()
  const rawMrrLabel = String(
    deal.wonMrrLabel ||
      formatCatchBoardPence(deal.wonMrrPence, { empty: '' }),
  ).trim()
  // List rows used to store "—" for missing MRR; never treat that as a real label.
  const mrrLabel = !rawMrrLabel || rawMrrLabel === '—' ? '' : rawMrrLabel
  const mrrPart = mrrLabel ? `${mrrLabel} MRR` : ''
  const primary = [plan, mrrPart].filter(Boolean).join(' · ') || null

  const goLiveLabel = String(
    deal.wonGoLiveLabel ||
      formatCatchBoardDate(deal.wonGoLiveDate, { empty: '' }),
  ).trim() || null

  if (!primary && !goLiveLabel) {
    return null
  }

  const titleParts = []
  if (plan) {
    titleParts.push(`Plan: ${plan}`)
  }
  if (mrrPart) {
    titleParts.push(mrrPart)
  }
  if (goLiveLabel) {
    titleParts.push(`Go-live ${goLiveLabel}`)
  }

  return {
    primary,
    goLiveLabel,
    title: titleParts.join(' · '),
  }
}

/**
 * First name (or email local-part) for compact card display.
 */
export function formatCatchBoardTouchedByName(lastTouchedBy = {}) {
  const name = String(lastTouchedBy?.name || '').trim()
  if (name) {
    return name.split(/\s+/)[0]
  }

  const email = String(lastTouchedBy?.email || '').trim()
  if (!email) {
    return ''
  }

  return email.split('@')[0] || ''
}

/**
 * Relative age for card face (Just now / 2h ago / Yesterday / 3d ago).
 */
export function formatCatchBoardRelativeTime(value, now = dayjs()) {
  if (!value) {
    return ''
  }

  const parsed = dayjs(value)
  const nowAt = dayjs(now)
  if (!parsed.isValid() || !nowAt.isValid()) {
    return ''
  }

  const diffDays = nowAt.startOf('day').diff(parsed.startOf('day'), 'day')
  if (diffDays <= 0) {
    const hours = Math.max(0, nowAt.diff(parsed, 'hour'))
    if (hours < 1) {
      const minutes = Math.max(0, nowAt.diff(parsed, 'minute'))
      if (minutes < 1) {
        return 'Just now'
      }
      return `${minutes}m ago`
    }
    return hours === 1 ? '1h ago' : `${hours}h ago`
  }

  if (diffDays === 1) {
    return 'Yesterday'
  }

  if (diffDays < 7) {
    return `${diffDays}d ago`
  }

  return parsed.format('D MMM')
}

/**
 * Compact card line: "Sarah · Call note · 2h ago"
 */
export function formatCatchBoardLastTouchedLabel(
  {
    lastTouchedBy,
    lastTouchedAt,
    lastActivitySummary,
  } = {},
  now = dayjs(),
) {
  const who = formatCatchBoardTouchedByName(lastTouchedBy)
  const when = formatCatchBoardRelativeTime(lastTouchedAt, now)
  const summary = String(lastActivitySummary || '')
    .replace(/\s+/g, ' ')
    .trim()

  const parts = []
  if (who) {
    parts.push(who)
  }
  if (summary) {
    parts.push(summary)
  }
  if (when) {
    parts.push(when)
  }

  return parts.join(' · ')
}

export function mapCatchBoardDealListItem(deal = {}, { now } = {}) {
  const dealId = deal.dealId || deal._id || null
  const lastTouchedBy = mapContactSnapshot(deal.lastTouchedBy)
  const lastTouchedAt = deal.lastTouchedAt || null
  const lastActivitySummary = deal.lastActivitySummary || ''
  const readiness = isCatchBoardReadiness(deal.readiness) ? deal.readiness : null

  return {
    key: dealId,
    dealId,
    name: deal.name || '',
    companyName: String(deal.companyName || '').trim(),
    stage: deal.stage || null,
    position: Number(deal.position) || 0,
    dealValuePence: deal.dealValuePence ?? null,
    potentialDealValuePence: deal.potentialDealValuePence ?? null,
    dealValueLabel: formatCatchBoardPence(deal.dealValuePence),
    potentialDealValueLabel: formatCatchBoardPence(deal.potentialDealValuePence),
    ownerAccountId: deal.ownerAccountId || null,
    ownerName: deal.ownerName || '',
    primaryVenueId: deal.primaryVenueId || null,
    primaryVenueName: deal.primaryVenueName || '',
    linkedVenueCount: Number(deal.linkedVenueCount) || 0,
    nextFollowUpAt: deal.nextFollowUpAt || null,
    expectedCloseDate: deal.expectedCloseDate || null,
    priority: deal.priority || null,
    readiness,
    readinessNote: deal.readinessNote || '',
    wonPlan: deal.wonPlan ?? null,
    wonMrrPence: deal.wonMrrPence ?? null,
    wonMrrLabel: formatCatchBoardPence(deal.wonMrrPence, { empty: '' }),
    wonGoLiveDate: deal.wonGoLiveDate || null,
    wonGoLiveLabel: formatCatchBoardDate(deal.wonGoLiveDate, { empty: '' }),
    lastTouchedBy,
    lastTouchedAt,
    lastActivitySummary,
    lastTouchedLabel: formatCatchBoardLastTouchedLabel(
      {
        lastTouchedBy,
        lastTouchedAt,
        lastActivitySummary,
      },
      now,
    ),
    updatedAt: deal.updatedAt || null,
  }
}

export function mapCatchBoardDeal(deal = {}) {
  const listItem = mapCatchBoardDealListItem(deal)

  return {
    ...listItem,
    createdByAccountId: deal.createdByAccountId || null,
    companyName: deal.companyName || '',
    description: deal.description || '',
    leadSource: deal.leadSource ?? null,
    leadSourceDetail: deal.leadSourceDetail || '',
    linkedVenueIds: Array.isArray(deal.linkedVenueIds)
      ? deal.linkedVenueIds.map(String)
      : [],
    linkedOpportunityIds: Array.isArray(deal.linkedOpportunityIds)
      ? deal.linkedOpportunityIds.map(String)
      : [],
    economicBuyers: Array.isArray(deal.economicBuyers)
      ? deal.economicBuyers.map(mapContactSnapshot)
      : [],
    userBuyers: Array.isArray(deal.userBuyers)
      ? deal.userBuyers.map(mapContactSnapshot)
      : [],
    wonPlan: deal.wonPlan ?? null,
    wonMrrPence: deal.wonMrrPence ?? null,
    wonGoLiveDate: deal.wonGoLiveDate || null,
    lostReason: deal.lostReason ?? null,
    lostReasonNote: deal.lostReasonNote || '',
    stageChangedAt: deal.stageChangedAt || null,
    closedAt: deal.closedAt || null,
    deletedAt: deal.deletedAt || null,
    createdAt: deal.createdAt || null,
    activityLog: Array.isArray(deal.activityLog)
      ? deal.activityLog.map(mapActivityLogEntry)
      : [],
  }
}

function emptyColumnTotals() {
  return CATCH_BOARD_STAGES.reduce((acc, stage) => {
    acc[stage] = {
      stage,
      dealCount: 0,
      dealValuePence: 0,
      potentialDealValuePence: 0,
      dealValueLabel: formatCatchBoardPence(0),
      potentialDealValueLabel: formatCatchBoardPence(0),
    }
    return acc
  }, {})
}

export function mapCatchBoardColumnTotals(columnTotalsByStage = {}) {
  const totals = emptyColumnTotals()

  Object.entries(columnTotalsByStage || {}).forEach(([stage, row]) => {
    if (!totals[stage]) {
      return
    }

    const dealValuePence = Number(row?.dealValuePence) || 0
    const potentialDealValuePence = Number(row?.potentialDealValuePence) || 0

    totals[stage] = {
      stage,
      dealCount: Number(row?.dealCount) || 0,
      dealValuePence,
      potentialDealValuePence,
      dealValueLabel: formatCatchBoardPence(dealValuePence),
      potentialDealValueLabel: formatCatchBoardPence(potentialDealValuePence),
    }
  })

  return totals
}

/**
 * Group list deals into stage columns (sorted by position).
 * @param {object[]} deals
 */
export function groupCatchBoardDealsByStage(deals = []) {
  const columns = CATCH_BOARD_STAGES.reduce((acc, stage) => {
    const meta = getCatchBoardStageMeta(stage)
    acc[stage] = {
      stage,
      meta,
      deals: [],
    }
    return acc
  }, {})

  deals.forEach(deal => {
    const stage = deal?.stage
    if (!columns[stage]) {
      return
    }
    columns[stage].deals.push(deal)
  })

  CATCH_BOARD_STAGES.forEach(stage => {
    columns[stage].deals.sort((a, b) => {
      const positionDelta = (Number(a.position) || 0) - (Number(b.position) || 0)
      if (positionDelta !== 0) {
        return positionDelta
      }
      return String(a.dealId || '').localeCompare(String(b.dealId || ''))
    })
  })

  return columns
}

export function mapCatchBoardListResponse(data = {}) {
  const deals = Array.isArray(data.deals)
    ? data.deals.map(mapCatchBoardDealListItem)
    : []

  const totals = {
    dealCount: Number(data.totals?.dealCount) || deals.length,
    dealValuePence: Number(data.totals?.dealValuePence) || 0,
    potentialDealValuePence: Number(data.totals?.potentialDealValuePence) || 0,
  }

  return {
    deals,
    columns: groupCatchBoardDealsByStage(deals),
    filters: data.filters || {},
    totals: {
      ...totals,
      dealValueLabel: formatCatchBoardPence(totals.dealValuePence),
      potentialDealValueLabel: formatCatchBoardPence(
        totals.potentialDealValuePence,
      ),
      // Pass through API momentum as-is (display helpers live in catchBoardMomentum).
      momentum: data.totals?.momentum || null,
    },
    columnTotalsByStage: mapCatchBoardColumnTotals(data.columnTotalsByStage),
  }
}

/**
 * Rebuild board view-model from a flat deals list (after local drag).
 * @param {Array<object>} [deals]
 * @param {object} [filters]
 * @param {{ momentum?: object|null }} [options]
 */
export function rebuildCatchBoardBoardState(
  deals = [],
  filters = {},
  options = {},
) {
  const normalized = Array.isArray(deals) ? deals : []
  const columns = groupCatchBoardDealsByStage(normalized)

  const rawColumnTotals = CATCH_BOARD_STAGES.reduce((acc, stage) => {
    const stageDeals = columns[stage]?.deals || []
    acc[stage] = {
      stage,
      dealCount: stageDeals.length,
      dealValuePence: stageDeals.reduce(
        (sum, deal) => sum + (Number(deal.dealValuePence) || 0),
        0,
      ),
      potentialDealValuePence: stageDeals.reduce(
        (sum, deal) => sum + (Number(deal.potentialDealValuePence) || 0),
        0,
      ),
    }
    return acc
  }, {})

  const totals = {
    dealCount: normalized.length,
    dealValuePence: normalized.reduce((sum, deal) => {
      if (!isCatchBoardOpenStage(deal.stage)) {
        return sum
      }
      return sum + (Number(deal.dealValuePence) || 0)
    }, 0),
    potentialDealValuePence: normalized.reduce((sum, deal) => {
      if (!isCatchBoardOpenStage(deal.stage)) {
        return sum
      }
      return sum + (Number(deal.potentialDealValuePence) || 0)
    }, 0),
  }

  const momentum =
    options.momentum !== undefined ? options.momentum : null

  return {
    deals: normalized,
    columns,
    filters,
    totals: {
      ...totals,
      dealValueLabel: formatCatchBoardPence(totals.dealValuePence),
      potentialDealValueLabel: formatCatchBoardPence(
        totals.potentialDealValuePence,
      ),
      momentum,
    },
    columnTotalsByStage: mapCatchBoardColumnTotals(rawColumnTotals),
  }
}

export function mapCatchBoardDealContext(context = {}) {
  return {
    dealId: context.dealId || null,
    primaryVenueId: context.primaryVenueId || null,
    linkedVenueIds: Array.isArray(context.linkedVenueIds)
      ? context.linkedVenueIds.map(String)
      : [],
    duplicateVenueWarnings: Array.isArray(context.duplicateVenueWarnings)
      ? context.duplicateVenueWarnings.map(warning => ({
          venueId: warning?.venueId ? String(warning.venueId) : null,
          deals: Array.isArray(warning?.deals)
            ? warning.deals.map(deal => ({
                dealId: deal?.dealId ? String(deal.dealId) : null,
                name: deal?.name || '',
                stage: deal?.stage || null,
              }))
            : [],
        }))
      : [],
    linkedOpportunities: Array.isArray(context.linkedOpportunities)
      ? context.linkedOpportunities
      : [],
    venues: Array.isArray(context.venues)
      ? context.venues.map(mapCatchBoardVenueContext)
      : [],
  }
}

/**
 * Normalize one venue block from GET /deals/:id/context.
 * @param {object} venue
 */
export function mapCatchBoardVenueContext(venue = {}) {
  const snapshot = venue.snapshot || {}
  const usersPayload = venue.users || {}
  const notesPayload = venue.notes || {}
  const opportunitiesPayload = venue.opportunities || {}

  const users = Array.isArray(usersPayload)
    ? usersPayload
    : Array.isArray(usersPayload.users)
      ? usersPayload.users
      : []
  const notes = Array.isArray(notesPayload)
    ? notesPayload
    : Array.isArray(notesPayload.notes)
      ? notesPayload.notes
      : []
  const opportunities = Array.isArray(opportunitiesPayload)
    ? opportunitiesPayload
    : Array.isArray(opportunitiesPayload.opportunities)
      ? opportunitiesPayload.opportunities
      : []

  return {
    venueId: venue.venueId ? String(venue.venueId) : null,
    isPrimary: Boolean(venue.isPrimary),
    snapshot: {
      venueId: snapshot.venueId
        ? String(snapshot.venueId)
        : venue.venueId
          ? String(venue.venueId)
          : null,
      name: snapshot.name || snapshot.venueName || '',
      plan: snapshot.plan || '',
      channel: snapshot.channel || '',
      phone: snapshot.phone || '',
      ordersInPeriod: snapshot.ordersInPeriod ?? null,
      upgradeScore: snapshot.upgradeScore ?? null,
      riskScore: snapshot.riskScore ?? null,
      lastOrderedAt: snapshot.lastOrderedAt || null,
      activeSuppliers: snapshot.activeSuppliers ?? null,
      notesCount: snapshot.notesCount ?? null,
      xeroConnected: snapshot.xeroConnected ?? null,
    },
    snapshotMeta: venue.snapshotMeta || null,
    users: users.map(user => ({
      accountId: user.accountId || null,
      name: user.name || '',
      email: user.email || '',
      role: user.role || '',
      isOwner: Boolean(user.isOwner),
      lastSeenLabel: user.lastSeenLabel || '',
    })),
    usersVenueName: usersPayload.venueName || snapshot.name || '',
    notes: notes.map(note => ({
      noteId: note.noteId || note._id || null,
      noteText: note.noteText || '',
      noteType: note.noteType || '',
      outcome: note.outcome || '',
      followUpAt: note.followUpAt || null,
      pinned: Boolean(note.pinned),
      createdAt: note.createdAt || null,
      authorName: note.author?.name || note.authorName || '',
    })),
    notesPagination: notesPayload.pagination || null,
    opportunities: opportunities.map(opp => ({
      id: opp.id || opp._id || null,
      type: opp.type || '',
      status: opp.status || '',
      occurredAt: opp.occurredAt || null,
      featureName: opp.featureName || '',
      suggestedFollowUp: opp.suggestedFollowUp || '',
      plan: opp.plan || '',
      venueName: opp.venue?.name || opp.venueName || '',
    })),
    opportunitiesPagination: opportunitiesPayload.pagination || null,
  }
}

/**
 * Build list query params from board filter UI state.
 * @param {{ ownerFilter?: string, readinessFilter?: string, search?: string, hideClosed?: boolean, stage?: string, ownerAccountId?: string }} [state]
 */
export function buildCatchBoardListQueryParams(state = {}) {
  const params = {}

  if (state.ownerFilter === 'mine') {
    params.mine = true
  } else if (state.ownerFilter === 'unassigned') {
    params.unassigned = true
  } else if (state.ownerAccountId) {
    params.ownerAccountId = state.ownerAccountId
  }

  if (
    state.readinessFilter &&
    state.readinessFilter !== 'all'
  ) {
    params.readiness = state.readinessFilter
  }

  const search = String(state.search || '').trim()
  if (search) {
    params.search = search
  }

  if (state.hideClosed) {
    params.hideClosed = true
  }

  if (state.stage) {
    params.stage = state.stage
  }

  return params
}
